import { Injectable } from '@angular/core';
import { BearerTokenFetchClient, TypedHash } from "@pnp/common";
import { sp, List, FieldAddResult, ItemAddResult, ItemUpdateResult } from "@pnp/sp";
import { of, from, Observable, throwError } from 'rxjs';
import { mergeMap, map, concatMap, tap, last } from 'rxjs/operators';
import { HelperService } from 'dnp-helper';


@Injectable({
  providedIn: 'root'
})
export class SharepointService {

  constructor(private help: HelperService) { }

  ping() {
    console.log('sharepoint service working ..');
  }

  setup(url: string, token: string) {
    sp.setup({
      sp: {
        baseUrl: `${url}`,
        headers: {
          "Accept": "application/json;odata=verbose",
          "Authorization": `Bearer ${token}`
        },
        fetchClientFactory: () => new BearerTokenFetchClient(token)
      }   
    });
    return of(sp.web.get());
  }

  add(listName: string, properties?: TypedHash<any>, listItemEntityTypeFullName?: string) {
    return from(sp.web.lists.getByTitle(listName).items.add(properties, listItemEntityTypeFullName));
  }

  delete(listName: string, id: number) {
    return from(sp.web.lists.getByTitle(listName).items.getById(id).delete());
  }

  getAll(listName: string, size?: number) {
    size = size || 1000;
    return from(sp.web.lists.getByTitle(listName).get())
      .pipe(
        mergeMap(list => from(sp.web.lists.getByTitle(listName).items.getAll(size))
        )
      );
  }

  getAllWithSelectAndExpand(listName: string, select: string[], expand:string[], normaliseData?: boolean,size?: number) {
    size = size || 1000;
    normaliseData = normaliseData || true;
    return from(sp.web.lists.getByTitle(listName).items
    .select(select.join(","))
    .expand(expand.join(","))
    .getAll(size))
    .pipe(map(objList=>{
      if(!normaliseData) return objList;
      let expandAttrs = select.filter(attr=>attr.indexOf('/') > -1);
      objList.forEach(obj=>{
        expandAttrs.forEach(attr=>{
          let attrs = attr.split('/');
          obj[attrs.join('')] = obj[attrs[0]][attrs[1]];
        })
        expandAttrs.forEach(attr=>{
          let attrs = attr.split('/');
          delete obj[attrs[0]];
        })
      })
      return objList;
    }));
  }

  getListFields(list: { Title: string }) {
  // Fields which can be updated
  const filter1 = `Hidden eq false and ReadOnlyField eq false`;
  return from(sp.web.lists.getByTitle(list.Title).fields.select('InternalName').filter(filter1).get());

    // return from(sp.web.lists.getByTitle(list.Title).fields.get());
  }

  getFiles(url: string) {
    return from(sp.web.getFolderByServerRelativeUrl(url).expand("Folders, Files").get());
  }

  getFilesAndListFields(folder: string, subsite: string){
		// console.log(folder,subsite);
		// const web = new Web(sp.web + '/' + subsite);
		return from(sp.web.getFolderByServerRelativeUrl(folder).expand("Folders,Files/ListItemAllFields").get());
	}
  getFileContent(url: string) {
    console.log('get file contents', url);
    return from(sp.web.getFileByServerRelativeUrl(url).getText())
      .pipe(map(rows => {
        let data = this.help.parseCSV(rows);
        // console.log('json csv data',data);
        return data;
      }));
    // .pipe(map(rows=>this.help.csvArrayToJSON(this.csv.parse(rows).data)));

  }



  getById(listName: string, id: number) {
    return from(sp.web.lists.getByTitle(listName).items.getById(id).get());
  }

  getWithQuery(query: any) {
    return of([]);
  }

  update(listName: string, id: number, properties?: TypedHash<any>, eTag?: string, listItemEntityTypeFullName?: string) {
    return from(sp.web.lists.getByTitle(listName).items.getById(id).update(properties, eTag, listItemEntityTypeFullName));
  }

  updateMultiple(listName: string, idList: number[], itemList?: TypedHash<any>[]){

    let listItem = sp.web.lists.getByTitle(listName);
    return from(itemList).pipe(
      concatMap(item=>from(listItem.items.getById(item['ID']).update(item))),
      tap((item: ItemUpdateResult)=>console.log( 'Item update  :: ',item))
    );
  
  }



  updateAll(listName: string, idList: number[], itemList?: TypedHash<any>[]){
      const batch = sp.web.createBatch();
      let list = sp.web.lists.getByTitle(listName);
      idList.forEach((id,i)=>list.items.getById(id).inBatch(batch).update(itemList[i]));
      return from(batch.execute());
    
  }

  upsert(id: any) { return of(); }

  simpleBatch() {
    let batch = sp.createBatch();
    sp.web.lists.getByTitle("Plans").items.inBatch(batch).get().then(console.log);
    batch.execute().then(() => console.log("All done!"));
  }

  createList(title: string, description?: string) {
    let template = 100; // https://docs.microsoft.com/en-us/previous-versions/office/sharepoint-server/ee541191(v=office.15)
    let enableContentTypes = true;
    return from(sp.web.lists.add(title, description, template, enableContentTypes));
  }


  deleteList(listName: string) {
    return from(sp.web.lists.getByTitle(listName).delete());
  }

  addColumn(list: { Title: string }, cols: { name: string, type: string, values: string[], required: boolean }[]) {
    let listItem = sp.web.lists.getByTitle(list.Title);
    return from(cols).pipe(
      concatMap(col=>{
          switch (col.type) {
            case 'Text': return from(listItem.fields.addText(col.name)); 
            case 'Number': return from(listItem.fields.addNumber(col.name)); 
            case 'TextArea': return from(listItem.fields.addMultilineText(col.name, 10));
            case 'Date': return from(listItem.fields.addDateTime(col.name));
            case 'Choice': return from(listItem.fields.addChoice(col.name, col.values));
            default: return from(listItem.fields.addText(col.name));
          }
      }),
      tap((values: FieldAddResult)=>console.log(' Column Added  :: ',values.data.EntityPropertyName)),
      last()
    );
  }

  addView(list: { Title: string }, cols: { name: string, type: string }[], viewName?: string) {
    console.log('building the view for the list ', list);
    viewName = viewName || 'All Item - Ergo';
    let result: any = Promise.resolve();
    let listItem = sp.web.lists.getByTitle(list.Title);
    try {
      result = result.then(()=>listItem.views.add(viewName, false, {
        RowLimit: 10,
        DefaultView: true,
        ViewQuery: "<OrderBy><FieldRef Name='Modified' Ascending='False' /></OrderBy>"
      }));
      
      let validCols = result.then(()=>  result = listItem.fields.get());
      console.log('validCols',validCols)  

      cols.forEach(col => result = result.then(()=>listItem.views.getByTitle(viewName).fields.add(col.name)))
    } catch (error) { return throwError(error); }
    return of({text: `${cols.length} field(s) added to ${viewName} on ${list.Title}`});

  }

  addMultiple(listName: string, objList:any[]): Observable<ItemAddResult>{
    let listItem = sp.web.lists.getByTitle(listName);
    return from(objList).pipe(
      concatMap(obj=>from(listItem.items.add(obj))),
      tap((values: ItemAddResult)=>console.log(' Item Added  :: ',values)),
      last()
    );
  }

  addBatch(listItem: { Title: string }, objList: any[]): Observable<any> {
    let itemList: IBatchCreationData[] = objList.map(obj => { return { item: obj, result: null, error: null } });

    // let list = await sp.web.lists.getByTitle(listItem.Title).get();
    // let itemEntityType = await list.getListItemEntityTypeFullName();

    return from(sp.web.lists.getByTitle(listItem.Title).get())
      .pipe(
        mergeMap(
          (list: List) => from(this.executeAddBatch(listItem.Title, list['EntityTypeName'], itemList))
        )
      );

  }


  executeAddBatch(listTitle: string, itemEntityType: string, data: IBatchCreationData[], index = 0, batchSize?) {
    batchSize = batchSize || 50;
    console.log('executeBatch ', listTitle, itemEntityType, data, index, batchSize);
    let promises = [];
    let list = sp.web.lists.getByTitle(listTitle);
    for (index = 1; data.length > index + 1; index++) {
      let batch = sp.web.createBatch();
      for (let len = index + batchSize; index < len && index < data.length; index += 1) {
        let dataItem = data[index];
        let success = (function (res) { this.result = res; }).bind(dataItem);
        let error = (function (err) { this.error = err; }).bind(dataItem);

        // list.items.inBatch(batch).add(dataItem.item, itemEntityType)
        list.items.inBatch(batch).add(dataItem.item)
          .then(success)
          .catch(error);

      }
      console.log(`Processing (${index} of ${data.length})... `)
      promises.push(batch.execute());
    }
    return promises.reduce((p, fn) => p.then(fn), Promise.resolve());
    
  };

  readFile(url: string, subsite?: string) {
		url = url.replace('https://qbe.sharepoint.com','');
		subsite = subsite || 'DataManagement';
		// const web = new Web(sp.web + '/' + subsite);
		// console.log('getFileByServerRelativeUrl ::',url);
		return from(sp.web.getFileByServerRelativeUrl(url).getBuffer());
	}

	readCSVFileFromURL(url: string, subsite?: string, offset?: number) {
		subsite = subsite ? subsite : '';
		// const web = new Web(sp.web + '/' + subsite+'/');
		return from(sp.web
		.getFileByServerRelativeUrl(encodeURIComponent(url))
		.getText())
		.pipe(
			map(rows=>this.help.parseCSV(rows), offset),
			// tap(console.log)
		);
  }
  


  public read(url, sheetNames: string[], subsite?: string, options?: {header?: any, range?: number, defVal?:any}[]){
		// startRow = startRow || 0;
		
		return this.readFile(url,subsite)
		.pipe(map((blob)=>{
      return this.help.blobToSheetObj(blob,sheetNames,url,options);
		}));

  }
  
  getAllFoldersFromURL(url: string, subsite: string){
		// const web = new Web(sp.web + '/' + subsite);
		return from(sp.web.getFolderByServerRelativeUrl(url).expand("Folders").get())
			.pipe(map(obj=>obj.Folders));
  }
  
  uploadFile(folder: string, fileName: string, objList: any[], subsite?: string){
		subsite = subsite ? subsite : '';
		// const web = new Web(sp.web + '/' + subsite+'/');
		let blob = new Blob([this.help.jsonToCSV(objList)],{type: 'text/csv'});
		return from(sp.web.getFolderByServerRelativeUrl(folder).files.add(fileName,blob,true))
	}


 
}

interface IBatchCreationData {
  item: any;
  result: any;
  error: any;
}
export interface ListItem {
  id: string;
  link: string;
  subsite: string;
}

export interface BaseItem {
  ID: string;
  Created: string;
  Modified: string;
  Title: string;
  Author: string;
  ModifiedBy: string;
}
